/**
 * テキスト・オン・パスのグリフ配置計算（pure モジュール、2026-07-10 追加）。
 *
 * 背景（detonate 中の文字被り問題の根本修正）:
 * 旧実装（`render/text.ts` 内）は弧長固定送り `pos += w + tracking` で
 * グリフを配置していた。グリフは接線に回転させた**剛体の直線ボックス**として
 * 描かれるため、warp された凹カーブでは弧長間隔が保たれていても
 * ユークリッド距離（弦）が縮み、隣接グリフが回転しながら食い込んでいた。
 *
 * 本モジュールは配置を 2 つの原理で置き換える:
 *
 * 1. **弦ベース衝突回避送り** — nominal 送り後、隣接グリフ中心間の
 *    ユークリッド距離が `(w_prev + w)/2 + tracking + 回転パディング` を
 *    満たすまで弧長位置を単調に前進させる。回転パディングは
 *    `fontSize × 0.75 × |sin(Δθ/2)|`（Δθ = 隣接回転差）で、凹みでボックスが
 *    互いに倒れ込む分を補償する。
 * 2. **弦接線** — グリフの回転角をミクロセグメントの接線ではなく
 *    「グリフ自身の弦」（`pointAt(pos)` → `pointAt(pos + w)` の方向）から
 *    取る。~4px のポリラインセグメント 1 本に回転が支配される接線ノイズを
 *    構造的に排除する（Illustrator 等のテキスト・オン・パス標準実装と同じ）。
 *
 * リピート / ジャスティファイの挙動は旧実装と同型を保つ:
 * - 1 周に収まらない場合は中央 1 周 + クリップ
 * - 収まる場合は floor(周長 / 実消費長) 周を配置し、残り slack を周境界に
 *   均等分配
 *
 * DOM / Canvas 非依存の pure 関数群。幅測定（`ctx.measureText`）は呼び出し側
 * （`render/text.ts`）の責務で、本モジュールは数値配列だけを受け取る。
 */

import type { SilhouettePoint, TextPathData } from './types';

/** 1 グリフ分の配置結果。 */
export interface GlyphPlacement {
  /** 元テキストの文字 index（リピート時は `i % chars.length` 済み）。 */
  charIndex: number;
  /** グリフ中心の X 座標（px）。 */
  x: number;
  /** グリフ中心の Y 座標（px）。 */
  y: number;
  /** グリフの回転角（rad、グリフ弦の方向）。 */
  angle: number;
}

/**
 * グリフの衝突判定ボックスの高さ係数（fontSize 比）。
 * 描画はベースラインを `-0.25 × fontSize` に置く（`render/text.ts`）ため、
 * グリフ本体はローカル y ∈ [-0.95f, -0.25f] を占める。その高さ 0.7f。
 */
const BOX_HEIGHT_COEFF = 0.7;

/**
 * 衝突判定ボックス中心のローカル y オフセット係数（fontSize 比）。
 * ベースライン -0.25f + 高さの半分 0.35f = -0.6f。
 */
const BOX_CENTER_Y_COEFF = -0.6;

/**
 * 負 tracking をボックス幅に織り込む際の下限係数。
 * `effectiveW = max(w + trackingPx, w × 0.5)` — ユーザが tracking で意図的に
 * 詰めた分は衝突とみなさない（既存の「負値で詰め」semantics の維持）が、
 * 幅半分未満への崩壊は防ぐ。
 */
const MIN_EFFECTIVE_W_COEFF = 0.5;

/** 衝突回避時に弧長位置を前進させる 1 ステップ（px）。 */
const PUSH_STEP = 0.5;

/** 衝突境界の二分法精密化の反復回数。8 回で粗ステップの 1/256 精度。 */
const COLLISION_BISECT_STEPS = 8;

/** ジャスティファイ spacing の二分法反復回数（末尾クリップ明滅の防止）。
 * 12 回で粒度 = slack/reps の 1/4096 — 後方の周に累積しても sub-px。
 * 粗いと spacing の量子化段差 × 周番号 が接線方向のガタつきになる。 */
const SPACING_BISECT_STEPS = 12;

/**
 * reps 昇格のヒステリシスマージン。`totalLength / L1` が
 * `前フレーム reps + この値` を越えるまで昇格を遅延する。pulse の
 * 周長振動（±数 %）による flapping を吸収できる大きさ。
 */
const REPS_UPGRADE_MARGIN = 1.15;

/** `computeGlyphPlacements` の配置オプション。 */
export interface PlacementOptions {
  /**
   * 前フレームで採用した reps（ヒステリシス用）。指定すると昇格が
   * `REPS_UPGRADE_MARGIN` 分だけ遅延し、周長振動での flapping を防ぐ。
   */
  preferredReps?: number;

  /**
   * ジャスティファイ spacing への乗数（0..1、default 1）。
   * 1 = 従来の均等配分。下げると全テキストが起点側へ詰まり、経路終端
   * （seam の手前）に空きが生まれる。detonate 中のオーバーシュート演出
   * （renderer の envelope）が駆動する。
   */
  spacingScale?: number;

  /**
   * 基本 reps に加えて配置**試行**する追加周回数（default 0）。
   * 追加分は空きがなければ経路終端でクリップされる（= 見た目は従来と
   * 同一）。`spacingScale` で空きが生まれた分だけ seam から流れ込む。
   */
  extraReps?: number;

  /**
   * `true` のとき reps の**昇格を禁止**する（降格は物理的必然なので常に許可）。
   * オーバーシュート演出の squeeze が浅い区間で使う: 昇格に伴う
   * ジャスティファイ再配分（spacing 境界数の変化）はリング全体に見えるため、
   * squeeze が深く再配分が不可視になるまで昇格を保留する。
   */
  holdReps?: boolean;
}

/** `computeGlyphPlacements` の結果。 */
export interface PlacementResult {
  /** グリフ配置列。 */
  placements: GlyphPlacement[];
  /** 採用したリピート数（中央 1 周ブランチでは 0）。次フレームの
   * `preferredReps` に渡す。 */
  reps: number;
}

/**
 * 旧実装互換の終端許容差（px）。最後の文字が浮動小数誤差で弾かれるのを
 * 防ぐ（旧 `render/text.ts` の `totalLength + 0.5` と同値）。
 */
const END_TOLERANCE = 0.5;

/** 経路上の補間点（内部用）。 */
interface PathPoint {
  x: number;
  y: number;
}

/** 衝突判定用に保持する直前グリフの状態（内部用）。 */
interface PrevGlyph {
  /** 衝突判定ボックスの 4 頂点（world 座標）。 */
  corners: Vec2Like[];
  /** ボックスの 2 軸（単位ベクトル、SAT 用）。 */
  axes: Vec2Like[];
}

/** 内部用の軽量 2D ベクトル。 */
interface Vec2Like {
  x: number;
  y: number;
}

/** `placeRun` の入力コンテキスト（内部用）。 */
interface RunContext {
  points: SilhouettePoint[];
  totalLength: number;
  widths: number[];
  trackingPx: number;
  fontSize: number;
}

/**
 * 累積距離 `target` の経路上の点を**線形補間つき**で返す。
 *
 * `samplePath`（`silhouette.ts`）はスナップ（補間なし）だが、弦接線の
 * 計算には連続な位置が必要なため本モジュールは補間版を内部に持つ。
 * 最終サンプル点以降（`target > 末尾.cumulative`）は末尾 → 先頭
 * （cumulative = totalLength）の閉路辺上で補間する。
 *
 * @param points `TextPathData.points`（cumulative 昇順）。
 * @param totalLength 経路全長。
 * @param target 累積距離（呼び出し側で `[0, totalLength]` に clamp 済み想定）。
 */
function pointAt(points: SilhouettePoint[], totalLength: number, target: number): PathPoint {
  const n = points.length;
  const first = points[0];
  const last = points[n - 1];
  // 呼び出し側で n >= 2 を保証しているが、型の都合で防御。
  if (!first || !last) return { x: 0, y: 0 };

  const t = Math.max(0, Math.min(totalLength, target));

  // 末尾サンプル以降は閉路の最終辺（末尾 → 先頭）で補間。
  if (t >= last.cumulative) {
    const span = totalLength - last.cumulative;
    if (span < 1e-9) return { x: last.x, y: last.y };
    const u = (t - last.cumulative) / span;
    return { x: last.x + (first.x - last.x) * u, y: last.y + (first.y - last.y) * u };
  }

  // 下限二分探索: cumulative >= t となる最初の点。
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const p = points[mid];
    if (!p || p.cumulative < t) lo = mid + 1;
    else hi = mid;
  }
  const b = points[lo];
  const a = points[lo - 1];
  if (!b) return { x: first.x, y: first.y };
  if (!a || lo === 0) return { x: b.x, y: b.y };
  const span = b.cumulative - a.cumulative;
  if (span < 1e-9) return { x: b.x, y: b.y };
  const u = (t - a.cumulative) / span;
  return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
}

/**
 * 弧長位置 `arc` から幅 `w` のグリフの中心と弦接線を計算する。
 *
 * 位置は経路上の `arc + w/2` の点（旧実装と同じ）、回転はグリフ弦
 * （`arc` → `arc + w` の方向）から取る。弦が退化（幅 0 等）した場合は
 * 直近の経路サンプル接線に頼らず 0 を返す（呼び出し側で幅 0 グリフは
 * 実質不可視なので影響なし）。
 */
function glyphPose(
  ctxRun: RunContext,
  arc: number,
  w: number,
): { x: number; y: number; angle: number } {
  const { points, totalLength } = ctxRun;
  const center = pointAt(points, totalLength, arc + w / 2);
  const pA = pointAt(points, totalLength, arc);
  const pB = pointAt(points, totalLength, arc + w);
  const dx = pB.x - pA.x;
  const dy = pB.y - pA.y;
  const angle = dx * dx + dy * dy < 1e-12 ? 0 : Math.atan2(dy, dx);
  return { x: center.x, y: center.y, angle };
}

/**
 * グリフの衝突判定ボックス（回転矩形）を構築する。
 *
 * 描画と同じモデル: アンカー（経路上の点）を原点、弦方向を +x とする
 * ローカル座標で、幅 `w`・高さ `0.7f` のボックスが中心 `(0, -0.6f)` に
 * 位置する（ベースライン `-0.25f` 補正込み）。
 *
 * 負 tracking はボックス幅へ織り込む（`w + trackingPx`、下限 `w × 0.5`）。
 * ユーザが意図的に詰めた分を衝突扱いすると直線経路で負 tracking が
 * 無効化されてしまうため。正 tracking はボックスに入れない（間隔は
 * nominal 送りが既に担保し、ボックスは曲率起因の食い込みだけを防ぐ）。
 */
function glyphBox(
  ctxRun: RunContext,
  pose: { x: number; y: number; angle: number },
  w: number,
): PrevGlyph {
  const { fontSize, trackingPx } = ctxRun;
  const effectiveW = Math.max(w + Math.min(0, trackingPx), w * MIN_EFFECTIVE_W_COEFF);
  const cos = Math.cos(pose.angle);
  const sin = Math.sin(pose.angle);
  const hw = effectiveW / 2;
  const hh = (fontSize * BOX_HEIGHT_COEFF) / 2;
  const cy = fontSize * BOX_CENTER_Y_COEFF;
  // ボックス中心（ローカル (0, cy) を world へ）。
  const cxW = pose.x - sin * cy;
  const cyW = pose.y + cos * cy;
  const ex: Vec2Like = { x: cos * hw, y: sin * hw };
  const ey: Vec2Like = { x: -sin * hh, y: cos * hh };
  return {
    corners: [
      { x: cxW + ex.x + ey.x, y: cyW + ex.y + ey.y },
      { x: cxW - ex.x + ey.x, y: cyW - ex.y + ey.y },
      { x: cxW - ex.x - ey.x, y: cyW - ex.y - ey.y },
      { x: cxW + ex.x - ey.x, y: cyW + ex.y - ey.y },
    ],
    axes: [
      { x: cos, y: sin },
      { x: -sin, y: cos },
    ],
  };
}

/** 頂点列を軸に射影した [min, max] を返す（SAT 用）。 */
function projectOntoAxis(corners: Vec2Like[], axis: Vec2Like): [number, number] {
  let mn = Number.POSITIVE_INFINITY;
  let mx = Number.NEGATIVE_INFINITY;
  for (const p of corners) {
    const d = p.x * axis.x + p.y * axis.y;
    if (d < mn) mn = d;
    if (d > mx) mx = d;
  }
  return [mn, mx];
}

/**
 * 2 つの回転矩形が交差しないか判定する（分離軸定理）。
 *
 * 矩形同士なので候補軸は両者の 2 軸ずつの計 4 本。いずれかの軸で射影が
 * 分離していれば非交差。中心距離の近似ではなく実ボックスで判定するのが
 * 本修正の核心 — S 字カーブでは平行なボックスが斜めに積み重なり、
 * 中心距離が十分でも食い込むケースがあるため。
 */
function boxesSeparated(a: PrevGlyph, b: PrevGlyph): boolean {
  for (const axis of [...a.axes, ...b.axes]) {
    const [a0, a1] = projectOntoAxis(a.corners, axis);
    const [b0, b1] = projectOntoAxis(b.corners, axis);
    if (a1 <= b0 || b1 <= a0) return true;
  }
  return false;
}

/**
 * 配置済みグリフボックスの空間ハッシュ（uniform grid）。
 *
 * 衝突判定を**隣接ペアだけでなく配置済み全グリフ**に対して行うための
 * 加速構造。テキスト経路は detonate 中の深い凹み（谷）で自分自身に
 * 接近するため、谷の両壁に載った文字同士（経路上は遠く離れた非隣接
 * グリフ）が交差しうる。grid で近傍候補だけを SAT 判定することで
 * O(N) を保つ。
 */
interface Collider {
  collides(box: PrevGlyph): boolean;
  insert(box: PrevGlyph): void;
}

/** `cellSize` 間隔の uniform grid collider を作る。 */
function createCollider(cellSize: number): Collider {
  const grid = new Map<string, PrevGlyph[]>();

  const cellRange = (box: PrevGlyph): [number, number, number, number] => {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const p of box.corners) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return [
      Math.floor(minX / cellSize),
      Math.floor(minY / cellSize),
      Math.floor(maxX / cellSize),
      Math.floor(maxY / cellSize),
    ];
  };

  /** box の AABB が覆う grid セルの候補ボックスを重複なしで集める。 */
  const candidatesFor = (box: PrevGlyph): Set<PrevGlyph> => {
    const [x0, y0, x1, y1] = cellRange(box);
    const seen = new Set<PrevGlyph>();
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const bucket = grid.get(`${cx},${cy}`);
        if (!bucket) continue;
        for (const other of bucket) seen.add(other);
      }
    }
    return seen;
  };

  return {
    collides(box: PrevGlyph): boolean {
      for (const other of candidatesFor(box)) {
        if (!boxesSeparated(other, box)) return true;
      }
      return false;
    },
    insert(box: PrevGlyph): void {
      const [x0, y0, x1, y1] = cellRange(box);
      for (let cx = x0; cx <= x1; cx++) {
        for (let cy = y0; cy <= y1; cy++) {
          const key = `${cx},${cy}`;
          const bucket = grid.get(key);
          if (bucket) bucket.push(box);
          else grid.set(key, [box]);
        }
      }
    },
  };
}

/**
 * `start` から `count` グリフを貪欲配置する 1 走行。
 *
 * 各グリフは nominal 位置から始め、**配置済みのどのグリフとも**SAT で
 * 分離するまで弧長を前進させる（単調・有界・決定的）。隣接グリフの
 * 曲率起因の食い込みだけでなく、経路の谷で向かい合う非隣接グリフの
 * 交差も防ぐ。前進しても衝突が解けないまま `limit` を超えるグリフは
 * そこで打ち切り（旧実装のクリップと同じ挙動）。
 *
 * @param repBoundarySpacing 1 周ごとに周境界へ加算するスペーシング
 *        （ジャスティファイ。dry-run では 0）。
 * @returns 配置列と、最後のグリフの後端 + tracking までの弧長（消費長）。
 */
function placeRun(
  ctxRun: RunContext,
  start: number,
  count: number,
  limit: number,
  repBoundarySpacing: number,
): { placements: GlyphPlacement[]; endPos: number; overflowed: boolean } {
  const { widths, trackingPx, fontSize } = ctxRun;
  const n = widths.length;
  const placements: GlyphPlacement[] = [];
  const collider = createCollider(fontSize * 2);
  // 衝突が続く間の前進幅。谷を丸ごと跨ぐケースがあるため PUSH_STEP より
  // 粗くして反復回数を抑える（配置精度への影響は数 px で視認不能）。
  const pushStep = Math.max(PUSH_STEP, fontSize * 0.05);
  let pos = start;

  for (let j = 0; j < count; j++) {
    const charIndex = j % n;
    const w = widths[charIndex] ?? 0;

    let arc = pos;
    let pose = glyphPose(ctxRun, arc, w);
    let box = glyphBox(ctxRun, pose, w);
    // 粗いステップで衝突しない位置を探す。lastColliding を記録して
    // 後段の二分法で境界を詰める。
    let lastColliding = -1;
    while (collider.collides(box)) {
      lastColliding = arc;
      arc += pushStep;
      if (arc + w > limit) break;
      pose = glyphPose(ctxRun, arc, w);
      box = glyphBox(ctxRun, pose, w);
    }
    if (arc + w > limit) {
      return { placements, endPos: pos, overflowed: true };
    }
    // 二分法精密化（2026-07-10 平滑化 F2）: 粗ステップ（≈2px）のままだと
    // 連続変形中に push 量が段差状に変わり、グリフがフレーム間でガタつく。
    // ［最後に衝突した位置, クリア位置］を 8 回の二分で sub-0.01px まで
    // 詰めることで、push 必要量が形状変化に対して連続になる。
    if (lastColliding >= 0) {
      let lo = lastColliding;
      let hi = arc;
      for (let b = 0; b < COLLISION_BISECT_STEPS; b++) {
        const mid = (lo + hi) / 2;
        const midPose = glyphPose(ctxRun, mid, w);
        const midBox = glyphBox(ctxRun, midPose, w);
        if (collider.collides(midBox)) lo = mid;
        else hi = mid;
      }
      arc = hi;
      pose = glyphPose(ctxRun, arc, w);
      box = glyphBox(ctxRun, pose, w);
    }

    placements.push({ charIndex, x: pose.x, y: pose.y, angle: pose.angle });
    collider.insert(box);
    pos = arc + w + trackingPx;

    // 周境界（テキスト 1 周の最後の文字の後）にジャスティファイ分を加算。
    if (charIndex === n - 1) pos += repBoundarySpacing;
  }
  return { placements, endPos: pos, overflowed: false };
}

/**
 * テキスト経路に沿ったグリフ配置列を計算する（本モジュールの公開 API）。
 *
 * アルゴリズム（3 パス、いずれも同じ貪欲走行 `placeRun` を使う）:
 * 1. **dry-run** — 位置 0 から 1 周分（`widths.length` グリフ）を配置して
 *    実消費長 `L1` を測る。収まらなければ**中央 1 周ブランチ**:
 *    nominal 長で中央合わせした開始位置から配置し、入る分だけ返す。
 * 2. **reps 決定** — `reps = floor(totalLength / L1)`。配置してみて
 *    あふれる場合は reps を 1 ずつ減らす（最少 1、それでも入らなければ
 *    中央 1 周ブランチへ）。
 * 3. **最終配置** — 実消費長との差 slack を `slack / reps` として
 *    各周境界に均等分配して配置し直す（旧実装のジャスティファイと同型）。
 *
 * @param pathData `buildTextPath` の返り値。
 * @param widths 文字ごとの advance 幅（px、`ctx.measureText` 由来）。
 * @param trackingPx 字間（px、`fontSize × tracking / 100`）。
 * @param fontSize フォントサイズ（px）。回転パディングの基準。
 * @param options ヒステリシス等の配置オプション。
 * @returns 配置列と採用 reps。経路退化・幅 0 などでは空配列 + reps 0。
 */
/**
 * reps（リピート数）の決定。`floor(ratio)` を基本に、ヒステリシス
 * （`preferredReps` + `REPS_UPGRADE_MARGIN`）と昇格禁止（`holdReps`）を適用。
 * 降格（preferred が入らない）は placeRun の overflow 側で強制される。
 */
function decideReps(ratio: number, options: PlacementOptions): number {
  const candidate = Math.max(1, Math.floor(ratio));
  const preferred = options.preferredReps;
  if (preferred === undefined || preferred < 1 || candidate <= preferred) return candidate;
  // holdReps: 昇格全面禁止（オーバーシュート演出の浅 squeeze 区間）。
  // 通常時: 比がマージンを越えるまで昇格を遅延（flapping 防止）し、
  // 越えても 1 フレーム 1 段まで（一括昇格の再配分ポップを分散）。
  if (options.holdReps === true || ratio < preferred + REPS_UPGRADE_MARGIN) return preferred;
  return Math.min(candidate, preferred + 1);
}

export function computeGlyphPlacements(
  pathData: TextPathData,
  widths: number[],
  trackingPx: number,
  fontSize: number,
  options: PlacementOptions = {},
): PlacementResult {
  const n = widths.length;
  const { points, totalLength } = pathData;
  if (n === 0 || points.length < 2 || totalLength <= 0) return { placements: [], reps: 0 };

  const nominalLen = widths.reduce((a, b) => a + b, 0) + n * trackingPx;
  if (nominalLen <= 0) return { placements: [], reps: 0 };

  const ctxRun: RunContext = { points, totalLength, widths, trackingPx, fontSize };
  const limit = totalLength + END_TOLERANCE;

  /** 中央 1 周ブランチ（旧 drawCenteredSingle と同型）: nominal 長で中央合わせ。 */
  const centeredSingle = (): PlacementResult => {
    const start = Math.max(0, (totalLength - nominalLen) / 2);
    return { placements: placeRun(ctxRun, start, n, totalLength, 0).placements, reps: 0 };
  };

  // パス 1: dry-run で 1 周の実消費長を測る。
  const dry = placeRun(ctxRun, 0, n, limit, 0);
  if (dry.overflowed || dry.endPos <= 0) return centeredSingle();

  // パス 2: reps を決める。実消費長ベースなので旧実装より安全側（少なめ）。
  //
  // ヒステリシス（2026-07-10 平滑化 F3）: pulse で周長が振動すると
  // floor の閾値付近で reps が毎サイクル flapping し、テキスト 1 周分が
  // 出現/消滅を繰り返す。`preferredReps`（前フレームの採用値）がある場合、
  // 昇格は比が preferred + REPS_UPGRADE_MARGIN を越えたときまで遅延する。
  // 降格は必須（入らないものは置けない）ので従来どおり。
  const ratio = totalLength / dry.endPos;
  let reps = decideReps(ratio, options);
  let packed = placeRun(ctxRun, 0, n * reps, limit, 0);
  while (packed.overflowed && reps > 1) {
    reps -= 1;
    packed = placeRun(ctxRun, 0, n * reps, limit, 0);
  }
  if (packed.overflowed) return centeredSingle();

  // パス 3: slack を周境界に均等分配して最終配置。
  //
  // spacing で位置がずれると曲率の異なる区間に乗って消費長が微増し、
  // 末尾のグリフが limit からあふれて落ちることがある。落ちる/落ちないが
  // フレームごとに切り替わると seam 付近の文字が明滅する（2026-07-10
  // 平滑化で顕在化）ため、「基本 n×reps グリフが全数入る最大の spacing」を
  // 二分法で求める（spacing=0 は packed で成立済みなので必ず解がある）。
  //
  // オーバーシュート演出（spacingScale < 1 / extraReps > 0）:
  // spacing を意図的に詰めて経路終端に空きを作り、追加周回分のグリフを
  // クリップ許容で続けて配置する。成立判定は**基本分の配置数**で行う
  // （追加分のあふれは正常系）。
  const spacingScale = Math.max(0, Math.min(1, options.spacingScale ?? 1));
  const extraReps = Math.max(0, Math.floor(options.extraReps ?? 0));
  const totalCount = n * (reps + extraReps);
  const baseCount = n * reps;

  const slack = Math.max(0, totalLength - packed.endPos);
  const spacingFull = (slack / reps) * spacingScale;
  let finalRun = placeRun(ctxRun, 0, totalCount, limit, spacingFull);
  if (finalRun.placements.length < baseCount) {
    let lo = 0;
    let hi = spacingFull;
    for (let b = 0; b < SPACING_BISECT_STEPS; b++) {
      const mid = (lo + hi) / 2;
      const trial = placeRun(ctxRun, 0, totalCount, limit, mid);
      if (trial.placements.length < baseCount) hi = mid;
      else {
        lo = mid;
        finalRun = trial;
      }
    }
    // lo で成立した最後の trial が finalRun に入っている。lo=0 まで
    // 縮んでも成立しなかった場合のみ packed（spacing なし）へ退避。
    if (finalRun.placements.length < baseCount) finalRun = packed;
  }
  return { placements: finalRun.placements, reps };
}
